import {
	computed,
	onMounted,
	onUnmounted,
	reactive,
	ref
} from 'vue'

// Animation constants
const HORIZONTAL_SPEED = 4.0
const VERTICAL_SPEED = 1.3
const SAMPLE_LENGTH = 1
const DOT_PADDING = 0
const REVERSE_ANIMATION_DELAY = 0.4
const SCROLL_DIRECTION_DOWN = 'down'
const SCROLL_DIRECTION_UP = 'up'

export function useFiberAnimation(
	startTriggerVP = 0.6,
	endTriggerVP = 0.4,
	horizontalSpeed = HORIZONTAL_SPEED,
	verticalSpeed = VERTICAL_SPEED
) {
	// DOM refs
	const pathContainerRef = ref<HTMLElement | null>(null)
	const path1Ref = ref<SVGPathElement | null>(null)
	const path2Ref = ref<SVGPathElement | null>(null)
	const dot1Ref = ref<SVGGElement | null>(null)
	const dot2Ref = ref<SVGGElement | null>(null)

	// Reactive state
	const dot1Position = reactive({
		x: 0,
		y: 0
	})
	const dot2Position = reactive({
		x: 0,
		y: 0
	})
	const dotsVisible = ref(false)

	const prevDistDot1 = ref(0)
	const prevDistDot2 = ref(0)
	const prevScrollTop = ref(0)
	const isAnimating = ref(false)
	const scrollingDirection = ref(SCROLL_DIRECTION_DOWN)
	const hasPassedEndTrigger = ref(false)
	const dynamicStartTrigger = ref<number | null>(null)
	const pathLength1 = ref(0)
	const pathLength2 = ref(0)

	// Computed values
	const completionPercentage = computed(() => {
		return pathLength1.value
			? Math.min(100, Math.round((prevDistDot1.value / pathLength1.value) * 100))
			: 0
	})
	const startTriggerPoint = computed(() => window.innerHeight * startTriggerVP)
	const endTriggerPoint = computed(() => window.innerHeight * endTriggerVP)

	// Helpers
	function checkOverlap(dotRect: any, sectionRect: DOMRect): boolean {
		const noOverlap = dotRect.right < sectionRect.left
			|| dotRect.left > sectionRect.right
			|| dotRect.bottom < sectionRect.top
			|| dotRect.top > sectionRect.bottom
		return !noOverlap
	}

	const checkTriggerPoints = () => {
		if (!pathContainerRef.value) return

		const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop
		const viewportHeight = window.innerHeight

		// We need to get the dot position from the path container and current position
		const pathContainerRect = pathContainerRef.value.getBoundingClientRect()

		// Create a virtual dot rect based on the SVG coordinate system
		const dotRect = {
			top: pathContainerRect.top + dot1Position.y,
			bottom: pathContainerRect.top + dot1Position.y,
			left: pathContainerRect.left + dot1Position.x,
			right: pathContainerRect.left + dot1Position.x
		}

		// Determine scrolling direction
		scrollingDirection.value = currentScrollTop > prevScrollTop.value ? SCROLL_DIRECTION_DOWN : SCROLL_DIRECTION_UP

		// Check if path container is out of viewport
		const isPathContainerVisible = !(
			pathContainerRect.bottom < 0
			|| pathContainerRect.top > viewportHeight
		)

		// If path container is out of viewport when scrolling down, stop animation
		if (!isPathContainerVisible && scrollingDirection.value === SCROLL_DIRECTION_DOWN) {
			isAnimating.value = false
			return
		}

		// Animation logic based on scrolling direction
		if (scrollingDirection.value === SCROLL_DIRECTION_DOWN) {
			// When scrolling down, check if dots have passed the start trigger point
			if (dotRect.top <= startTriggerPoint.value && !hasPassedEndTrigger.value) {
				isAnimating.value = true
			}

			// Reset dynamic trigger if we're scrolling down from the top
			if (prevDistDot1.value <= 0) {
				dynamicStartTrigger.value = null
			}
		} else if (scrollingDirection.value === SCROLL_DIRECTION_UP) {
			const endTriggerPointLocal = viewportHeight * endTriggerVP

			// When scrolling up after having reached end of path
			if (hasPassedEndTrigger.value) {
				// Use the End Trigger for reverse animation to maintain consistency
				if (dotRect.top >= endTriggerPointLocal) {
					isAnimating.value = true
					// console.log('Start animating in reverse: dots passed end trigger')
				}
			// eslint-disable-next-line @stylistic/brace-style
			}
			// If already animating and not at end, continue animation
			else if (isAnimating.value && !hasPassedEndTrigger.value && pathContainerRect.top < viewportHeight) {
				// Continue animation
			} else {
				// Don't start animating yet when first scrolling up
				isAnimating.value = false
			}

			// Reset the hasPassedEndTrigger flag only when we've animated all the way back to the beginning
			if (prevDistDot1.value <= 0) {
				hasPassedEndTrigger.value = false
				isAnimating.value = false // Stop animation once at beginning
				dynamicStartTrigger.value = null // Reset dynamic trigger
				// console.log('Animation reset: back at beginning')
			}
		}
	}

	const runUpdate = () => {
		if (!path1Ref.value || !path2Ref.value || !dot1Ref.value || !dot2Ref.value) return

		// Get current scroll position
		const scrollTop = window.pageYOffset || document.documentElement.scrollTop
		const scrollDelta = scrollTop - prevScrollTop.value

		// Check trigger points
		checkTriggerPoints()

		// Only run animation calculations if we're animating and scrolled significantly
		if (isAnimating.value && Math.abs(scrollDelta + REVERSE_ANIMATION_DELAY) >= 0.5) {
			/*
			* If the path has horizontal sections, enough of them can cause the dots to fall behind the viewport window
			* when scrolling down. To fix this, can do 2 things:
			* 1.) We will adjust the scale 'factor' (speed multiplier) for both horizontal and vertical directions
			* 2.) We will calculate the angle of the path and adjust the speed based on the current position's angle.
			*/
			const baseHorizontalScaleFactor = horizontalSpeed // NOTE: Change this to adjust horizontal traversal speed
			const baseVerticalScaleFactor = verticalSpeed // NOTE: Change this to adjust vertical traversal speed

			// Calculate path angle for proper scaling
			const p0 = path1Ref.value.getPointAtLength(Math.max(0, prevDistDot1.value - SAMPLE_LENGTH))
			const p1 = path1Ref.value.getPointAtLength(Math.min(pathLength1.value, prevDistDot1.value + SAMPLE_LENGTH))
			const dx = p1.x - p0.x
			const dy = p1.y - p0.y
			const angle = (dx === 0 && dy === 0)
				? (prevDistDot1.value > pathLength1.value / 2 ? -Math.PI / 2 : Math.PI / 2)
				: Math.atan2(dy, dx)
			const horizontalWeight = Math.abs(Math.cos(angle))
			const effectiveScaleFactor = baseVerticalScaleFactor
				+ horizontalWeight * (baseHorizontalScaleFactor - baseVerticalScaleFactor)

			// Calculate new dot positions based on scroll direction
			let deltaLength = scrollDelta * effectiveScaleFactor

			// When scrolling back up, make sure we animate in reverse
			if (scrollingDirection.value === SCROLL_DIRECTION_UP && hasPassedEndTrigger.value) {
				// Force reverse animation when scrolling up (adding delay to keep dots more centered in viewport)
				deltaLength = (scrollDelta + REVERSE_ANIMATION_DELAY) * effectiveScaleFactor
			}

			let dist1 = prevDistDot1.value + deltaLength
			dist1 = Math.max(0, Math.min(dist1, pathLength1.value))
			let dist2 = prevDistDot2.value + deltaLength
			dist2 = Math.max(0, Math.min(dist2, pathLength2.value))

			const point1 = path1Ref.value.getPointAtLength(dist1)
			const point2 = path2Ref.value.getPointAtLength(dist2)

			// Update dot positions
			dot1Position.x = point1.x
			dot1Position.y = point1.y
			dot2Position.x = point2.x
			dot2Position.y = point2.y
			dotsVisible.value = true

			// Store new positions
			prevDistDot1.value = dist1
			prevDistDot2.value = dist2

			// Check if we've reached the end after updating positions
			if (scrollingDirection.value === SCROLL_DIRECTION_DOWN && prevDistDot1.value >= pathLength1.value * 0.999) {
				isAnimating.value = false
				hasPassedEndTrigger.value = true

				// Store the current scroll position as the dynamic trigger point for reverse animation
				dynamicStartTrigger.value = window.pageYOffset || document.documentElement.scrollTop
				// console.log('Animation stopped: reached end of path. Scroll position:', dynamicStartTrigger.value)
			}
		}

		// Update scroll position for next frame for determining scroll direction
		prevScrollTop.value = scrollTop

		// if user scrolled to the bottom but path isn’t 100%, snap to end
		const scrollHeight = document.documentElement.scrollHeight
		const clientHeight = document.documentElement.clientHeight
		const maxScroll = scrollHeight - clientHeight

		if (
			scrollingDirection.value === SCROLL_DIRECTION_DOWN
			&& scrollTop >= maxScroll
			&& completionPercentage.value < 100
		) {
			// force 100%
			prevDistDot1.value = pathLength1.value
			prevDistDot2.value = pathLength2.value
			const endP1 = path1Ref.value!.getPointAtLength(pathLength1.value)
			const endP2 = path2Ref.value!.getPointAtLength(pathLength2.value)
			dot1Position.x = endP1.x
			dot1Position.y = endP1.y
			dot2Position.x = endP2.x
			dot2Position.y = endP2.y
			hasPassedEndTrigger.value = true
			isAnimating.value = false
		}

		// if user scrolled to the top but path isn’t 0%, snap to start
		if (
			scrollingDirection.value === SCROLL_DIRECTION_UP
			&& scrollTop <= 0
			&& completionPercentage.value > 0
		) {
			// force 0%
			prevDistDot1.value = 0
			prevDistDot2.value = 0
			const startP1 = path1Ref.value!.getPointAtLength(0)
			const startP2 = path2Ref.value!.getPointAtLength(0)
			// Set final positions
			dot1Position.x = startP1.x
			dot1Position.y = startP1.y
			dot2Position.x = startP2.x
			dot2Position.y = startP2.y
			// set the previous distance to 0
			prevDistDot1.value = 0
			prevDistDot2.value = 0
			// set the hasPassedEndTrigger to false
			// this will trigger the animation to start again
			// when the user scrolls down again
			hasPassedEndTrigger.value = false
			// set isAnimating to false
			// this will stop the animation
			// when the user scrolls up while the path is at 0%
			isAnimating.value = false
		}

		// Check for section intersections with dots
		// We need to create virtual dot rects based on SVG positions and container position
		const containerRect = pathContainerRef.value?.getBoundingClientRect() || new DOMRect()

		const dot1Rect = {
			top: containerRect.top + dot1Position.y - DOT_PADDING, // Add optional padding offset for dot size
			left: containerRect.left + dot1Position.x - DOT_PADDING,
			bottom: containerRect.top + dot1Position.y + DOT_PADDING,
			right: containerRect.left + dot1Position.x + DOT_PADDING
		}

		const dot2Rect = {
			top: containerRect.top + dot2Position.y - DOT_PADDING,
			left: containerRect.left + dot2Position.x - DOT_PADDING,
			bottom: containerRect.top + dot2Position.y + DOT_PADDING,
			right: containerRect.left + dot2Position.x + DOT_PADDING
		}

		// Find all sections
		const sections = document.querySelectorAll('.intersection-trigger')

		sections.forEach(section => {
			const sectionRect = section.getBoundingClientRect()
			const isDot1Overlapping = checkOverlap(dot1Rect, sectionRect)
			const isDot2Overlapping = checkOverlap(dot2Rect, sectionRect)

			// Activate section if either dot overlaps
			if (isDot1Overlapping || isDot2Overlapping) {
				section.classList.add('intersecting')
			} else {
				section.classList.remove('intersecting')
			}
		})
	}

	const initializePositions = () => {
		if (!path1Ref.value || !path2Ref.value || !dot1Ref.value || !dot2Ref.value) return

		pathLength1.value = path1Ref.value.getTotalLength()
		pathLength2.value = path2Ref.value.getTotalLength()

		const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop
		prevScrollTop.value = currentScrollTop

		// For initial dot position check, we need to look at path container
		const pathContainerRect = pathContainerRef.value?.getBoundingClientRect()
		if (!pathContainerRect) return

		// Set initial position based on state
		if (hasPassedEndTrigger.value) {
			// If we've completed the path before, keep dots at end
			prevDistDot1.value = pathLength1.value
			prevDistDot2.value = pathLength2.value
		} else if (pathContainerRect.top <= startTriggerPoint.value) {
			// If already past start trigger but not at end (meaning the dots are initially above the start trigger),
			// start animation and calculate based on scroll
			const scrollHeight = document.documentElement.scrollHeight
			const clientHeight = document.documentElement.clientHeight
			const maxScroll = scrollHeight - clientHeight
			const scrollFraction = maxScroll > 0 ? currentScrollTop / maxScroll : 0

			prevDistDot1.value = scrollFraction * pathLength1.value
			prevDistDot1.value = Math.max(0, Math.min(prevDistDot1.value, pathLength1.value))
			prevDistDot2.value = scrollFraction * pathLength2.value
			prevDistDot2.value = Math.max(0, Math.min(prevDistDot2.value, pathLength2.value))
		} else {
			// Start at the beginning of the path
			prevDistDot1.value = 0
			prevDistDot2.value = 0
		}

		// Update dot positions
		const initialPoint1 = path1Ref.value.getPointAtLength(prevDistDot1.value)
		const initialPoint2 = path2Ref.value.getPointAtLength(prevDistDot2.value)

		// Update SVG dot positions
		dot1Position.x = initialPoint1.x
		dot1Position.y = initialPoint1.y
		dot2Position.x = initialPoint2.x
		dot2Position.y = initialPoint2.y
		dotsVisible.value = true

		// Initial checks
		runUpdate()
	}

	let ticking = false
	function scrollHandler() {
		if (!ticking) {
			window.requestAnimationFrame(() => {
				runUpdate()
				ticking = false
			})
			ticking = true
		}
	}

	onMounted(() => {
		setTimeout(initializePositions, 100)
		window.addEventListener('scroll', scrollHandler, {
			passive: true
		})
		window.addEventListener('resize', initializePositions)
	})

	onUnmounted(() => {
		window.removeEventListener('scroll', scrollHandler)
		window.removeEventListener('resize', initializePositions)
	})

	return {
		pathContainerRef,
		path1Ref,
		path2Ref,
		dot1Ref,
		dot2Ref,
		dot1Position,
		dot2Position,
		dotsVisible,
		completionPercentage,
		scrollingDirection,
		isAnimating,
		hasPassedEndTrigger,
		dynamicStartTrigger,
		startTriggerPoint,
		endTriggerPoint
	}
}
